import { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Stack, Button, useMediaQuery, CircularProgress, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LayoutContext } from "../../context/LayoutContext";
import CampaignIcon from "@mui/icons-material/Campaign";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import { getTemplatesApi } from "../../api/chat.api";
import {
  createCampaignApi,
  updateCampaignApi,
  getCampaignApi,
  validateContactsApi,
} from "../../api/campaign.api";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast } from "../../utils/toast";

import CampaignDetailsSection from "./sections/CampaignDetailsSection";
import TemplateSelectionSection from "./sections/TemplateSelectionSection";
import ContactUploadSection from "./sections/ContactUploadSection";
import SendingSettingsSection from "./sections/SendingSettingsSection";


import IOSMobileFrame from "../Chat/Templates/IOSMobileFrame";
import WhatsAppAdsPreviewIOS from "../Chat/Templates/WhatsAppPreview";

const INITIAL_FORM = {
  name: "",
  tag: "",
  description: "",
  isReusable: false,
  sendMode: "instant",
  scheduledAt: null,
  sendSpeed: "medium",
  retryFailed: 0,
  optInConfirmed: false,
  dndFilter: true,
  removeDuplicates: true,
  trackDelivery: true,
  trackReadReceipts: true,
  trackClicks: false,
};

export default function CreateCampaign() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditMode = Boolean(editId);
  const { collapsed } = useContext(LayoutContext);
  const isMobile = useMediaQuery("(max-width:500px)");
  const sidebarWidth = isMobile ? 0 : collapsed ? 80 : 280;

  // Form state
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [loadingCampaign, setLoadingCampaign] = useState(false);

  // Templates
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variableMapping, setVariableMapping] = useState({});

  // Contacts
  const [contacts, setContacts] = useState([]);
  const [contactStats, setContactStats] = useState({
    total: 0,
    valid: 0,
    invalid: 0,
    duplicates: 0,
  });
  const [fieldMapping, setFieldMapping] = useState({ phone: "", name: "" });
  const [csvHeaders, setCsvHeaders] = useState([]);

  // Actions
  const [submitting, setSubmitting] = useState(false);
  const showPreview = useMediaQuery("(min-width:1100px)");

  // Build preview header with proper media mapping for the preview component
  const getPreviewHeader = () => {
    if (!selectedTemplate?.header) return {};
    const h = selectedTemplate.header;
    const format = h.format || h.type;
    if (format === "IMAGE" || format === "VIDEO" || format === "DOCUMENT") {
      return { ...h, media: { type: format, url: h.url || h.media?.url } };
    }
    return h;
  };

  // Build preview text with variable substitution
  const getPreviewBody = () => {
    if (!selectedTemplate) return "";
    let text = selectedTemplate.body?.text || "";
    (selectedTemplate.body?.variables || []).forEach((v) => {
      const mapped = variableMapping[v.index];
      const display = mapped ? `[${mapped}]` : `{{${v.index}}}`;
      text = text.replace(new RegExp(`\\{\\{${v.index}\\}\\}`, "g"), display);
    });
    return text;
  };

  // Load templates on mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Load campaign data in edit mode
  useEffect(() => {
    if (!editId) return;
    const loadCampaign = async () => {
      setLoadingCampaign(true);
      try {
        const res = await getCampaignApi(editId);
        const c = res.data?.data || res.data;
        if (!c) return;

        // Populate form fields
        setFormData({
          name: c.name || "",
          tag: c.tag || "",
          description: c.description || "",
          isReusable: c.isReusable || false,
          sendMode: c.sendMode || "instant",
          scheduledAt: c.scheduledAt || null,
          sendSpeed: c.sendSpeed || "medium",
          retryFailed: c.retryFailed ?? 0,
          optInConfirmed: c.optInConfirmed || false,
          dndFilter: c.dndFilter ?? true,
          removeDuplicates: c.removeDuplicates ?? true,
          trackDelivery: c.trackDelivery ?? true,
          trackReadReceipts: c.trackReadReceipts ?? true,
          trackClicks: c.trackClicks || false,
        });

        // Restore template selection (match from loaded templates list)
        if (c.template) {
          setSelectedTemplate({
            id: c.template.id,
            name: c.template.name,
            language: c.template.language,
            body: {
              text: c.template.bodyText || "",
              variables: c.template.variables || [],
            },
            header: c.template.header || {},
            footer: c.template.footer || {},
            buttons: c.template.buttons || [],
          });
        }

        // Restore variable mapping
        if (c.variableMapping) {
          setVariableMapping(c.variableMapping);
        }

        // Restore contacts
        if (c.contacts?.length) {
          const contactList = c.contacts.map((ct) => ({
            phone: ct.phone || "",
            name: ct.name || "",
            customFields: ct.customFields || {},
          }));
          setContacts(contactList);

          // Recalculate stats
          const seen = new Set();
          let valid = 0, invalid = 0, duplicates = 0;
          for (const ct of contactList) {
            const phone = (ct.phone || "").replace(/\D/g, "");
            if (phone.length < 10) { invalid++; continue; }
            if (seen.has(phone)) { duplicates++; continue; }
            seen.add(phone);
            valid++;
          }
          setContactStats({ total: contactList.length, valid, invalid, duplicates });
        }
      } catch {
        toast.error("Failed to load campaign");
        navigate("/campaign");
      } finally {
        setLoadingCampaign(false);
      }
    };
    loadCampaign();
  }, [editId]);

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await getTemplatesApi();
      const data = res.data?.data || res.data || [];
      setTemplates(data?.templates.filter((t) => t.status === "APPROVED"));
    } catch {
      toast.error("Failed to load templates");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (prev[field]) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return prev;
    });
  }, []);

  const handleSelectTemplate = useCallback((tpl) => {
    setSelectedTemplate(tpl);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.template;
      delete next.variableMapping;
      return next;
    });

    if (tpl?.body?.variables?.length) {
      const mapping = {};
      tpl.body.variables.forEach((v) => {
        mapping[v.index] = "";
      });
      setVariableMapping(mapping);
    } else {
      setVariableMapping({});
    }
  }, []);

  const handleMapVariable = useCallback((index, value) => {
    setVariableMapping((prev) => ({ ...prev, [index]: value }));
    setErrors((prev) => {
      if (prev.variableMapping) {
        const next = { ...prev };
        delete next.variableMapping;
        return next;
      }
      return prev;
    });
  }, []);

  const handleContactsParsed = useCallback((parsed) => {
    setContacts(parsed);
    // Run local validation
    const seen = new Set();
    let valid = 0;
    let invalid = 0;
    let duplicates = 0;

    for (const c of parsed) {
      const phone = (c.phone || "").replace(/\D/g, "");
      if (phone.length < 10) {
        invalid++;
        continue;
      }
      if (seen.has(phone)) {
        duplicates++;
        continue;
      }
      seen.add(phone);
      valid++;
    }

    setContactStats({ total: parsed.length, valid, invalid, duplicates });
    setErrors((prev) => {
      if (prev.contacts) {
        const next = { ...prev };
        delete next.contacts;
        return next;
      }
      return prev;
    });
  }, []);

  // Validation
  const validate = (mode = "send") => {
    const errs = {};

    if (!formData.name.trim()) errs.name = "Campaign name is required";

    if (mode === "draft") {
      setErrors(errs);
      return Object.keys(errs).length === 0;
    }

    if (!selectedTemplate) errs.template = "Select a WhatsApp template";

    if (mode === "reusable") {
      setErrors(errs);
      return Object.keys(errs).length === 0;
    }

    // Full send validation
    if (selectedTemplate?.body?.variables?.length) {
      const unmapped = selectedTemplate.body.variables.filter(
        (v) => !variableMapping[v.index]
      );
      if (unmapped.length > 0) {
        errs.variableMapping = `${unmapped.length} variable(s) not mapped`;
      }
    }

    if (contactStats.valid === 0) {
      errs.contacts = "Add at least one valid contact";
    }

    if (!formData.optInConfirmed) {
      errs.optInConfirmed = "Opt-in confirmation is required";
    }

    if (formData.sendMode === "scheduled") {
      if (!formData.scheduledAt) {
        errs.scheduledAt = "Schedule date/time is required";
      } else if (new Date(formData.scheduledAt) <= new Date()) {
        errs.scheduledAt = "Scheduled time must be in the future";
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildPayload = () => ({
    ...formData,
    template: selectedTemplate
      ? {
          id: selectedTemplate.id,
          name: selectedTemplate.name,
          language: selectedTemplate.language,
          bodyText: selectedTemplate.body?.text || "",
          variables: selectedTemplate.body?.variables || [],
          header: selectedTemplate.header || {},
          footer: selectedTemplate.footer || {},
          buttons: selectedTemplate.buttons || [],
        }
      : undefined,
    variableMapping,
    contacts: contacts
      .filter((c) => {
        const phone = (c.phone || "").replace(/\D/g, "");
        return phone.length >= 10;
      })
      .map((c) => ({
        phone: (c.phone || "").replace(/\D/g, ""),
        name: c.name || "",
        customFields: c.customFields || {},
      })),
  });

  const handleValidateContacts = async () => {
    if (contacts.length === 0) {
      toast.error("No contacts to validate");
      return;
    }
    setSubmitting(true);
    try {
      const res = await validateContactsApi({
        contacts: contacts.map((c) => ({ phone: c.phone, name: c.name })),
        removeDuplicates: formData.removeDuplicates,
      });
      const data = res.data?.data || res.data;
      setContactStats({
        total: data.total,
        valid: data.valid,
        invalid: data.invalid,
        duplicates: data.duplicates,
      });
      toast.success(`Validation complete: ${data.valid} valid contacts`);
    } catch {
      toast.error("Validation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async () => {
    // validation for save can be 'draft' (minimal) or stricter if desired.
    // Using 'draft' ensures we can save partial work.
    if (!validate("draft")) {
      toast.error("Please enter a campaign name");
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildPayload();
      // Always save as 'draft' status initially, or keep existing status if editing and it was already something else (though usually we start as draft)
      // Actually, if we are just saving, we probably want to ensure it's in a state that can be run later.
      // But 'draft' is the correct status for "saved but not sent".
      payload.status = "draft"; 

      if (isEditMode) {
        await updateCampaignApi(editId, payload);
      } else {
        await createCampaignApi(payload);
      }

      toast.success(isEditMode ? "Campaign updated" : "Campaign saved");
      navigate("/campaign");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save campaign");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingCampaign) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: "270px" }}>
      {/* Breadcrumbs */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
        <Typography
          onClick={() => navigate("/campaign")}
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: "#94a3b8",
            cursor: "pointer",
            "&:hover": { color: theme.palette.primary.main },
          }}
        >
          Campaigns
        </Typography>
        <NavigateNextIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: theme.palette.primary.main }}>
          {isEditMode ? "Edit Campaign" : "Create Campaign"}
        </Typography>
      </Stack>

      {/* Page Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: theme.palette.primary.main,
            color: "#fff",
          }}
        >
          <CampaignIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#1e293b" }}>
            {isEditMode ? "Edit Campaign" : "Create Campaign"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
            {isEditMode
              ? "Update your campaign details and settings"
              : "Set up and save your campaign"}
          </Typography>
        </Box>
      </Stack>

      {/* Two-column layout: Form left, Preview right */}
      <Grid container spacing={3} alignItems="flex-start">
        {/* Left column - Form sections */}
        <Grid size={{ xs: 12, lg: showPreview ? 8 : 12 }}>
          <CampaignDetailsSection
            formData={formData}
            errors={errors}
            onChange={handleChange}
          />

          <SendingSettingsSection
            formData={formData}
            errors={errors}
            onChange={handleChange}
          />
        </Grid>

        {/* Right column - Sticky iPhone preview */}
        {showPreview && (
          <Grid size={{ xs: 12, lg: 4 }}>
            <Box
              sx={{
                position: "sticky",
                top: 16,
                mb: 8,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#475569",
                  mb: 1.5,
                  textAlign: "center",
                }}
              >
                Live Preview
              </Typography>
              <IOSMobileFrame>
                <WhatsAppAdsPreviewIOS
                  data={{
                    header: getPreviewHeader(),
                    body: selectedTemplate
                      ? getPreviewBody()
                      : "Your WhatsApp message will appear here",
                    footer: typeof selectedTemplate?.footer === "string"
                      ? selectedTemplate.footer
                      : selectedTemplate?.footer?.text || "",
                    buttons: selectedTemplate?.buttons || [],
                    brand: formData.name || "Campaign",
                  }}
                />
              </IOSMobileFrame>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Action Buttons - fixed to bottom of viewport */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          right: 0,
          left: `${sidebarWidth}px`,
          bgcolor: "#fff",
          borderTop: "1px solid #e2e8f0",
          py: 1,
          px: 3,
          zIndex: 10,
          boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/campaign")}
            sx={{
              ...btnSx,
              borderColor: "#e2e8f0",
              color: "#475569",
              "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={submitting}
            sx={{
              ...btnSx,
              bgcolor: theme.palette.primary.main,
              color: "#fff",
              px: 4,
              "&:hover": { bgcolor: theme.palette.primary.dark },
            }}
          >
            {isEditMode ? "Update Campaign" : "Save Campaign"}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

const btnSx = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13,
  borderRadius: 2,
  px: 2.5,
};
